/**
 * CTA (Call to Action) type definitions and helpers.
 *
 * Based on breitling-storefront-next patterns for consistent
 * product card CTA handling across the multi-brand platform.
 */

import { FEATURES } from '@/lib/const';

// =============================================================================
// CTA Types
// =============================================================================

/**
 * CTA types from the order app / Algolia.
 */
export type OrderAppCtaType = 'ORDER' | 'PRE_ORDER' | 'NOTIFY' | 'NO_CTA';

const ORDER_APP_CTA_VALUES: readonly OrderAppCtaType[] = ['ORDER', 'PRE_ORDER', 'NOTIFY', 'NO_CTA'];

/** Parse a raw CTA string into a known OrderApp CTA type, if recognized. */
export function parseOrderAppCtaType(value?: string | null): OrderAppCtaType | undefined {
  if (!value) return undefined;

  const normalized = value.toUpperCase().replaceAll('-', '_');
  for (const cta of ORDER_APP_CTA_VALUES) {
    if (cta === normalized) {
      return cta;
    }
  }

  return undefined;
}

/**
 * Extended CTA types including display-only options.
 */
export type CtaType =
  | OrderAppCtaType
  | 'DISCOVER'
  | 'BOOK_APPOINTMENT'
  | 'ENGRAVING'
  | 'CONTACT_US';

/**
 * CTA style variants for button rendering.
 */
export type CtaVariant = 'primary' | 'secondary' | 'outline' | 'link' | 'contactUs';

// =============================================================================
// CTA Label Mappings
// =============================================================================

/**
 * Default CTA labels by type.
 * These can be overridden by i18n translations.
 */
export const CTA_LABELS: Record<CtaType, string> = {
  ORDER: 'Add to Bag',
  PRE_ORDER: 'Pre-Order Now',
  NOTIFY: 'Notify Me',
  DISCOVER: 'Discover',
  BOOK_APPOINTMENT: 'Book Appointment',
  ENGRAVING: 'Add Engraving',
  CONTACT_US: 'Contact Us',
  NO_CTA: 'Contact Us',
};

/**
 * CTA button variant mappings.
 * Determines the visual style of the CTA button.
 */
export const CTA_VARIANTS: Record<CtaType, CtaVariant> = {
  ORDER: 'outline',
  PRE_ORDER: 'outline',
  NOTIFY: 'secondary',
  DISCOVER: 'link',
  BOOK_APPOINTMENT: 'secondary',
  ENGRAVING: 'secondary',
  CONTACT_US: 'outline',
  NO_CTA: 'outline',
};

// =============================================================================
// CTA Helper Functions
// =============================================================================

/**
 * Normalize a CTA string from Algolia to a CtaType.
 * Handles case-insensitivity and unknown values.
 *
 * @param value - Raw CTA value from Algolia hit
 * @returns Normalized CtaType
 */
export function normalizeCtaType(value: string | undefined | null): CtaType {
  if (!value) return 'DISCOVER';

  const normalized = value.toUpperCase().replace(/-/g, '_') as CtaType;

  // Validate against known types
  if (normalized in CTA_LABELS) {
    return normalized;
  }

  // Default fallback
  return 'DISCOVER';
}

/**
 * Raw CTA value from Algolia/API before normalization.
 * Prefer this over `CtaType | string` — the union collapses to `string`.
 */
export type CtaTypeInput = string | undefined;

/**
 * Get the display label for a CTA type.
 *
 * @param ctaType - The CTA type
 * @param customLabels - Optional custom label overrides (for i18n)
 * @returns Display label string
 */
export function getCtaLabel(
  ctaType: CtaTypeInput,
  customLabels?: Partial<Record<CtaType, string>>
): string {
  const normalized = normalizeCtaType(ctaType);
  return customLabels?.[normalized] ?? CTA_LABELS[normalized];
}

/**
 * Get the button variant for a CTA type.
 *
 * @param ctaType - The CTA type
 * @returns Button variant string
 */
export function getCtaVariant(ctaType: CtaTypeInput): CtaVariant {
  const normalized = normalizeCtaType(ctaType);
  return CTA_VARIANTS[normalized];
}

/**
 * Check if a CTA type should show a button.
 *
 * @param ctaType - The CTA type
 * @returns Whether to show the CTA button
 */

export function shouldShowCta(_ctaType: CtaTypeInput): boolean {
  // All CTA types should show a button (NO_CTA shows "Contact Us")
  return true;
}

/**
 * Check if a CTA type represents an "add to cart" action.
 *
 * @param ctaType - The CTA type
 * @returns Whether the CTA adds to cart
 */
export function isAddToCartCta(ctaType: CtaTypeInput): boolean {
  const normalized = normalizeCtaType(ctaType);
  return normalized === 'ORDER' || normalized === 'PRE_ORDER';
}

/**
 * Check if a CTA type opens the contact form (discontinued / no buy path).
 *
 * Example Input:
 * ctaType="NO_CTA"
 *
 * Example Output:
 * true
 */
export function isContactUsCta(ctaType: CtaTypeInput): boolean {
  const normalized = normalizeCtaType(ctaType);
  return normalized === 'NO_CTA' || normalized === 'CONTACT_US';
}

/**
 * Check if a CTA type is a notification request.
 *
 * @param ctaType - The CTA type
 * @returns Whether the CTA is for notifications
 */
export function isNotifyCta(ctaType: CtaTypeInput): boolean {
  const normalized = normalizeCtaType(ctaType);
  return normalized === 'NOTIFY';
}

/**
 * Resolve the effective PDP primary CTA from OrderApp.
 * - Missing CTA → Contact Us (NO_CTA)
 * - ORDER + quantityAvailable missing or ≤ 0 → Notify Me
 *
 * Example Input:
 * ctaType="ORDER", quantityAvailable=0
 *
 * Example Output:
 * "NOTIFY"
 */
export function resolveEffectivePdpCta(
  ctaType: string | undefined | null,
  quantityAvailable?: number | null
): CtaType {
  if (!ctaType) return 'NO_CTA';

  const normalized = normalizeCtaType(ctaType);
  if (normalized === 'ORDER' && (quantityAvailable == null || quantityAvailable <= 0)) {
    return resolveNotifyMeForBrand('NOTIFY');
  }
  return resolveNotifyMeForBrand(normalized);
}

/**
 * Check if a CTA type is discovery/browse only.
 *
 * @param ctaType - The CTA type
 * @returns Whether the CTA is discovery-focused
 */
export function isDiscoveryCta(ctaType: CtaTypeInput): boolean {
  const normalized = normalizeCtaType(ctaType);
  return normalized === 'DISCOVER' || normalized === 'NO_CTA';
}

// =============================================================================
// Call to Buy (phone CTA)
// =============================================================================

/** phoneCTA values that enable the Call to Buy secondary CTA on PDPs. */
export const CALL_TO_BUY_PHONE_CTA_TYPES = ['ORDER', 'PRE_ORDER'] as const;

export type CallToBuyPhoneCtaType = (typeof CALL_TO_BUY_PHONE_CTA_TYPES)[number];

export function isCallToBuyPhoneCta(phoneCTA?: string | null): phoneCTA is CallToBuyPhoneCtaType {
  return CALL_TO_BUY_PHONE_CTA_TYPES.includes(phoneCTA as CallToBuyPhoneCtaType);
}

/**
 * Whether the PDP should show Call to Buy.
 * ORDER requires positive stock; PRE_ORDER does not (pre-order SKUs often report 0).
 */
export function shouldShowCallToBuy({
  phoneCTA,
  quantityAvailable,
  isCatalogMode,
}: {
  phoneCTA?: string | null;
  quantityAvailable?: number | null;
  isCatalogMode: boolean;
}): boolean {
  if (isCatalogMode || !isCallToBuyPhoneCta(phoneCTA)) {
    return false;
  }

  if (phoneCTA === 'PRE_ORDER') {
    return true;
  }

  return (quantityAvailable ?? 0) > 0;
}

/**
 * Whether Notify Me should be exposed (PDP primary/secondary, PLP/search cards).
 * Gallet V3 go-live defers Notify Me to V3.1 (BEC-24791, BEC-23128).
 * Re-enable via NEXT_PUBLIC_FEATURE_NOTIFY_ME_ENABLED=true (see FEATURES.enableNotifyMe).
 */
export function isNotifyMePdpEnabled(): boolean {
  return FEATURES.enableNotifyMe;
}

/** Maps NOTIFY → Contact Us when {@link isNotifyMePdpEnabled} is false. */
export function resolveNotifyMeForBrand(ctaType: CtaType): CtaType {
  if (!isNotifyMePdpEnabled() && ctaType === 'NOTIFY') {
    return 'NO_CTA';
  }
  return ctaType;
}
